/**
 * canvasStage — ce que l'écran affiché dit au canvas (#856).
 *
 * ## Le problème que ça résout
 *
 * Depuis que le canvas vit dans la coque et non plus dans l'écran assistant
 * (#856), il n'a plus de parent React qui lui passe des propriétés. Or il a
 * toujours besoin de deux choses que **seul l'écran qui affiche le fil
 * connaît** : la conversation affichée et le dossier sélectionné. L'assistant
 * les publie ; le dock « Demander à Hector » aussi, mais seulement quand Hector
 * prend le document de son fil (#1588) ; et le geste « ouvrir une pièce dans le
 * canvas » d'un dossier, pour le fil que l'avocat vient de choisir
 * (`canvasOuvertureDepuisUnDossier`).
 *
 * La session temps réel, elle, ne passe **pas** par ici : le canvas la retrouve
 * lui-même à partir du porteur (`CanvasResidence`). Elle y a transité un temps,
 * et c'était un défaut — la clé locale de l'écran repart à `null` à chaque
 * montage, si bien qu'un simple aller-retour pouvait publier `null` par-dessus
 * la session d'un stream en cours et faire perdre l'annonce de fin. Une valeur
 * qu'on peut redériver ne se transporte pas.
 *
 * Ce module est le canal par lequel l'écran les publie. Une seule règle en
 * gouverne la sémantique :
 *
 * > **La scène se gèle, elle ne se vide pas.**
 *
 * Quand l'avocat quitte `/assistant` pour consulter un dossier, l'écran se
 * démonte et cesse de publier — mais les valeurs publiées restent. C'est
 * exactement le comportement voulu : le document ouvert continue d'appartenir à
 * sa conversation pendant qu'on regarde ailleurs, et il la retrouve au retour.
 * Une seule exception, depuis #1588 : si Hector prend le document d'un fil
 * depuis le dock hors de l'assistant, le dock publie ce fil
 * (`publierLaScene`) — sans jamais rien effacer non plus.
 * Effacer ici reviendrait à orpheliner le document (brouillon enregistré sans
 * conversation, session temps réel qui ne peut plus rendre compte de la fin
 * d'un stream).
 *
 * La divergence entre « la conversation affichée » et « le porteur du
 * document » devient donc réelle avec ce ticket — jusqu'ici les deux
 * coïncidaient toujours, et c'est pourquoi elles portaient déjà deux noms
 * distincts depuis #862. La rendre **visible** à l'avocat est le sujet de #853 ;
 * les résidences exactes la rendent désormais indépendante par conversation.
 *
 * ## Ce que ce module ne fait PLUS (2026-08-13)
 *
 * Il **cédait** le canvas à l'écran affiché : publier la scène réattribuait le
 * document au passage. Deux symptômes, tous deux signalés à l'usage — le canvas
 * paraissait « ouvert partout », et il n'y avait aucun moyen de le renvoyer chez
 * lui. La possession se prend maintenant par un geste
 * (`ouvrirLeCanvasPourLAvocat`, `openCanvasForAgentWrite`), et l'affichage se
 * décide par `canvasVisibilite`.
 *
 * Depuis #1331 il ne reste **aucune** écriture du porteur ici. Ce module
 * adoptait encore un document qui n'appartenait à personne, au premier message
 * du fil ; un tel document ne peut plus naître, le Canvas ne s'ouvrant que dans
 * une conversation dont le serveur prouve l'existence
 * (`canvasConversationGate`). Ce fichier ne publie donc plus que la scène.
 */

import { useLayoutEffect } from 'react';
import { create } from 'zustand';
import { useCanvasEditorStore } from './canvasEditorStore';

export interface CanvasStageSnapshot {
  /**
   * Conversation affichée par l'écran qui publie : `?c=` de `/assistant`, ou le
   * fil local du dock quand Hector y prend le document (#1588).
   */
  conversationId: string | null;
  /** Dossier sélectionné dans le composeur, valeur initiale d'« Ouvrir ». */
  caseId: string | null;
}

interface CanvasStageStore extends CanvasStageSnapshot {
  publishStage: (next: CanvasStageSnapshot) => void;
}

export const useCanvasStageStore = create<CanvasStageStore>((set) => ({
  conversationId: null,
  caseId: null,
  publishStage: (next) => set(next),
}));

/**
 * Publie une scène, depuis React ou hors de React.
 *
 * **Le dossier ne bouge que si le document bouge.** Publier le dossier de
 * l'écran alors que le document est resté chez un autre porteur les ferait
 * diverger : l'enregistrement automatique — qui lit les deux — classerait le
 * brouillon de A sous le dossier de B (#1340). Un porteur nul ne protège aucun
 * brouillon : il n'y a alors rien qui puisse diverger, et le dossier de l'écran
 * est la seule valeur juste.
 *
 * Exportée pour le dock (#1588), qui publie la scène sur un signal du pool et
 * non à chaque rendu : la résidence du canvas la lit dès le déploiement, qui
 * suit ce signal dans la même tâche — un rendu React arriverait trop tard.
 */
export function publierLaScene(stage: CanvasStageSnapshot): void {
  const { conversationId, caseId } = stage;
  const porteur = useCanvasEditorStore.getState().holderConversationId;
  const documentSuitLEcran = porteur === null
    || porteur === conversationId
    || conversationId === null;
  useCanvasStageStore.getState().publishStage({
    conversationId,
    caseId: documentSuitLEcran ? caseId : useCanvasStageStore.getState().caseId,
  });
}

/**
 * Publie la scène depuis l'écran assistant (`AgentV3View`), à chaque changement
 * de fil ou de dossier.
 *
 * Le dock (`DossierCaseDiscoveryShell`) ne l'appelle PAS : il ne montre aucun
 * canvas, et publier dès qu'il affiche un fil faisait sélectionner, réhydrater
 * ou refroidir des documents sur une page qui n'en montre aucun (#1588). Il
 * publie par `publierLaScene`, seulement quand Hector prend le document de son
 * fil (`useBasculeDuDockVersLAssistant`).
 *
 * `useLayoutEffect` : la scène est publiée dans le commit même où l'écran
 * change de fil, avant la peinture — la résidence du canvas la lit pour décider
 * quel document poser dans l'emplacement que cet écran affiche.
 *
 * Pas de nettoyage : quitter l'écran gèle la scène (cf. docblock du module).
 *
 * **Un écran sans conversation ne réattribue pas le document.** `null` n'est pas
 * une conversation, c'est l'absence de conversation affichée — un fil neuf, un
 * retour sur `/assistant` sans `?c=`. La scène le publie tel quel, mais rien ici
 * n'écrit le porteur : un porteur remis à `null` voudrait dire « ce document
 * n'appartient plus à personne », et la suite serait immédiate — gabarit
 * neutralisé, clé de montage changée, SuperDoc reconstruit, et l'avocat perdrait
 * tout ce qui ne vit que dans l'éditeur.
 */
export function useCanvasStagePublisher(stage: CanvasStageSnapshot): void {
  const { conversationId, caseId } = stage;
  useLayoutEffect(() => {
    // **REGARDER N'EST PLUS PRENDRE** (2026-08-13). Jusqu'à cette date, afficher
    // une conversation lui cédait le document : passer de A à B faisait suivre
    // l'acte de A, et l'avocat voyait « son canvas ouvert partout ». La
    // possession se prend désormais par un geste — le bouton d'ouverture
    // (`ouvrirLeCanvasPourLAvocat`) ou une écriture d'agent
    // (`openCanvasForAgentWrite`) — et le canvas ne s'affiche que sur l'écran de
    // son porteur (`canvasVisibilite`). Ce module ne publie donc plus que la
    // scène.
    //
    // **L'ADOPTION TARDIVE A DISPARU** (#1331). Ce module rattrapait ici un
    // document ouvert sans porteur, en le donnant au premier message : entre
    // l'ouverture et ce rattrapage, le brouillon partait avec
    // `conversationId: null` et personne ne le rouvrait dans son fil. Le Canvas
    // ne s'ouvre plus que dans une conversation dont le serveur prouve
    // l'existence (`canvasConversationGate`), donc un document sans porteur ne
    // peut plus naître — il n'y a plus rien à adopter.
    publierLaScene({ conversationId, caseId });
  }, [conversationId, caseId]);
}
